import "assets/scss/global.scss";
// Dashboard grid + widget-body styles are loaded globally. A dashboard renders
// both as the standalone /dashboard route and embedded on container
// (workspace/project/bundle) screens via DashboardGrid; those consumers live in
// separate lazy route chunks, so a global import guarantees the grid layout and
// every widget-body style are present wherever DashboardGrid renders, giving an
// embedded dashboard the same appearance as the standalone screen.
// See operator-task-container-configurable-dashboards.
import "containers/Dashboard/DashboardView.scss";
import "components/Notification/Notification.scss";
import "focus-visible";
import { useEffect } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { Provider } from "react-redux";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PersistGate } from "redux-persist/lib/integration/react";
import { persistor, store } from "store";
import RouterManager from "utils/RouterManager";
import { ErrorBoundary } from "utils/RouterManager/screens/Error";
import { reportError } from "utils/reportError";

const ScrollToTop = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        // `.main__content` is the page scroll container (see Main.scss); `.main`
        // itself does not scroll. Fall back to the <main> element / document for
        // layouts that render without the inner content wrapper.
        const scrollContainer =
            document.querySelector<HTMLElement>(".main__content") ||
            document.getElementsByTagName("main")?.[0] ||
            document.documentElement ||
            document.body;
        scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
    }, [pathname]);

    return null;
};

function App() {
    return (
        <PersistGate persistor={persistor}>
            <ToastContainer position="bottom-right" closeOnClick={false} />
            <Provider store={store}>
                <Router>
                    <ScrollToTop />
                    <RouterManager />
                </Router>
            </Provider>
        </PersistGate>
    );
}

const AppWithErrorBoundary = withErrorBoundary(App, {
    FallbackComponent: ErrorBoundary,
    onError(error, info) {
        reportError(error, { componentStack: info.componentStack, source: "app" });
    },
});

export default AppWithErrorBoundary;
